import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';
import { PrivacyDataSection } from '../../components/PrivacyDataSection';
import { HelpFaqSection } from '../../components/HelpFaqSection';

export function BusinessSettings() {
  const { headers, user } = useAuthContext();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').startsWith('sv') ? 'sv' : 'en';
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState('');

  useEffect(() => {
    axios.get(`${API}/business/categories?lang=${lang}`, { headers }).then(r => setCategories(r.data.items || [])).catch(() => {});
  }, [headers, lang]);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API}/business/categories`, { name: newCat }, { headers });
      setNewCat('');
      const res = await axios.get(`${API}/business/categories?lang=${lang}`, { headers });
      setCategories(res.data.items || []);
      toast.success('Category added');
    } catch (e) {
      toast.error('Failed to add category');
    }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/business/categories/${id}`, { headers });
      setCategories(prev => prev.filter(c => (c._id || c.id) !== id));
      toast.success('Category deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="business-settings">
      <h2 className="text-lg font-semibold">Settings</h2>

      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Payment Categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category name" data-testid="new-category-input" />
            <Button size="sm" onClick={addCategory} data-testid="add-category-btn">Add</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <div key={c.code || c._id || c.id} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-sm">
                {c.icon} {c.name}
                {!c.is_system && (
                  <button onClick={() => deleteCategory(c._id || c.id)} className="text-muted-foreground hover:text-rose-600 ml-1">&times;</button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Data (GDPR) */}
      <PrivacyDataSection headers={headers} user={user} />

      {/* Help & FAQ */}
      <HelpFaqSection headers={headers} />
    </div>
  );
}
